<template>
  <GridBlockBase
    id="admin-program-settings"
    data-test-id="admin-program-settings"
    :title="!props.id || props.id.length < 1 ? t('admin.programSettings.newProgram') : t('admin.programSettings.title')"
  >
    <template #body v-if="programRef?.settings">
      <div class="program-settings-form">
        <div class="settings-section">
          <p class="settings-section__title" data-test-id="setting-title-general">
            {{ t('admin.programSettings.section.general') }}
          </p>
          <CustomInput
            type="text"
            id="program-name"
            data-test-id="program-name"
            :label="t('admin.programSettings.form.name.label')"
            :placeholder="t('admin.programSettings.form.name.placeholder')"
            @emit:change="programNameChangeHandler"
            :value="programRef.name"
          />
          <div class="program-description-field" data-test-id="program-description">
            <label class="input-label" id="program-description-label">
              {{ t('admin.programSettings.form.description.label') }}
            </label>
            <TiptapEditor
              aria-labelledby="program-description-label"
              v-model="programDescriptionRef"
              :placeholder="t('admin.programSettings.form.description.placeholder')"
            />
          </div>
          <div class="program-settings-form__grid">
            <CustomInput
              type="text"
              id="mentor-name"
              data-test-id="mentor-name"
              :label="t('admin.programSettings.form.mentorName.label')"
              :placeholder="t('admin.programSettings.form.mentorName.placeholder')"
              @emit:change="mentorNameChangeHandler"
              :value="programRef.settings.i18nMentorName"
            />
            <CustomInput
              type="text"
              id="mentee-name"
              data-test-id="mentee-name"
              :label="t('admin.programSettings.form.menteeName.label')"
              :placeholder="t('admin.programSettings.form.menteeName.placeholder')"
              @emit:change="menteeNameChangeHandler"
              :value="programRef.settings.i18nMenteeName"
            />
          </div>
          <div>
            <label class="input-label">{{ t('admin.programSettings.form.programPeriod.label') }}</label>
            <div class="program-settings-form__grid">
              <CustomInput
                type="date"
                id="program-period-start"
                data-test-id="program-period-start"
                :label="t('admin.programSettings.form.programPeriod.start.label')"
                :placeholder="t('admin.programSettings.form.programPeriod.start.placeholder')"
                @emit:change="startAtChangeHandler"
                :value="formattedStartAtRef"
              />
              <CustomInput
                type="date"
                id="program-period-end"
                data-test-id="program-period-end"
                :label="t('admin.programSettings.form.programPeriod.end.label')"
                :placeholder="t('admin.programSettings.form.programPeriod.end.placeholder')"
                @emit:change="endAtChangeHandler"
                :value="formattedEndAtRef"
              />
            </div>
          </div>
        </div>
        <ProgramTypeSetting
          v-if="accountRef?.isConstelStaff && programRef.id"
          :program-id="programRef.id"
          :value="programRef.settings.programType"
          @saved="programTypeSavedHandler"
        />
        <div class="settings-section">
          <p class="settings-section__title">{{ t('admin.programSettings.section.chat') }}</p>
          <CustomInput
            type="number"
            id="default-inactivity-days"
            :label="t('admin.programSettings.form.defaultInactivityDays.label')"
            :placeholder="t('admin.programSettings.form.defaultInactivityDays.placeholder')"
            @emit:change="defaultInactivityDaysChangeHandler"
            :value="
              programRef.settings.defaultInactivityDays !== undefined
                ? String(programRef.settings.defaultInactivityDays)
                : ''
            "
          />
        </div>
        <div class="settings-section">
          <div>
            <p class="settings-section__title">{{ t('admin.programSettings.section.chatModeration') }}</p>
            <CustomCheckbox
              id="contact-warning-enabled"
              :label="t('admin.programSettings.form.contactWarningEnabled.label')"
              data-test-id="checkbox-contact-warning-enabled"
              :defaultChecked="programRef.settings.moderationPiiEnabled"
              @emit:change="onModerationPiiEnabledChange"
            />
          </div>
        </div>
        <div class="settings-section">
          <p class="settings-section__title">{{ t('admin.programSettings.section.groups') }}</p>
          <CustomInput
            type="number"
            id="max-user-per-group"
            data-test-id="max-user-per-group"
            :label="t('admin.programSettings.form.maxUserPerGroup.label')"
            :placeholder="t('admin.programSettings.form.max.placeholder')"
            @emit:change="maxUserPerGroupChangeHandler"
            :value="
              programRef.settings.maxUserPerGroup !== undefined ? String(programRef.settings.maxUserPerGroup) : ''
            "
          />
          <div class="program-settings-form__grid">
            <CustomInput
              type="number"
              id="max-group-per-mentor"
              data-test-id="max-group-per-mentor"
              :label="t('admin.programSettings.form.maxGroupPerMentor.label')"
              :placeholder="t('admin.programSettings.form.max.placeholder')"
              @emit:change="maxGroupPerMentorChangeHandler"
              :value="
                programRef.settings.maxGroupPerMentor !== undefined ? String(programRef.settings.maxGroupPerMentor) : ''
              "
            />
            <CustomInput
              type="number"
              id="max-group-per-mentee"
              data-test-id="max-group-per-mentee"
              :label="t('admin.programSettings.form.maxGroupPerMentee.label')"
              :placeholder="t('admin.programSettings.form.max.placeholder')"
              @emit:change="maxGroupPerMenteeChangeHandler"
              :value="
                programRef.settings.maxGroupPerMentee !== undefined ? String(programRef.settings.maxGroupPerMentee) : ''
              "
            />
          </div>
        </div>
        <div class="settings-section">
          <p class="settings-section__title">{{ t('admin.programSettings.section.matching') }}</p>
          <CustomCheckbox
            id="matching-criteria-enabled"
            data-test-id="checkbox-matching-criteria"
            :label="t('admin.programSettings.form.matchingCriteriaEnabled.label')"
            @emit:change="onMatchingCriteriaEnabledChange"
            :defaultChecked="programRef.settings.matchingCriteriaEnabled"
          />
          <CustomCheckbox
            id="matching-auto-suggest-enabled"
            data-test-id="checkbox-matching-auto-suggest"
            :label="t('admin.programSettings.form.matchingAutoSuggestEnabled.label')"
            @emit:change="onMatchingAutoSuggestEnabledChange"
            :defaultChecked="programRef.settings.matchingAutoSuggestEnabled"
            :isDisabled="!programRef.settings.matchingCriteriaEnabled"
          />
          <CustomTextarea
            id="matching-additional-instructions"
            :label="t('admin.programSettings.form.matchingAdditionalInstructions.label')"
            :placeholder="t('admin.programSettings.form.matchingAdditionalInstructions.placeholder')"
            :rows="5"
            @emit:change="matchingAdditionalInstructionsChangeHandler"
            :value="programRef.settings.matchingAdditionalInstructions"
          />
          <CustomTextarea
            id="mentor-matching-criteria"
            :label="t('admin.programSettings.form.mentorMatchingCriteria.label')"
            :placeholder="t('admin.programSettings.form.mentorMatchingCriteria.placeholder')"
            :rows="5"
            @emit:change="mentorMatchingCriteriaChangeHandler"
            :value="programRef.settings.mentorMatchingCriteria"
          />
          <CustomTextarea
            id="mentee-matching-criteria"
            :label="t('admin.programSettings.form.menteeMatchingCriteria.label')"
            :placeholder="t('admin.programSettings.form.menteeMatchingCriteria.placeholder')"
            :rows="5"
            @emit:change="menteeMatchingCriteriaChangeHandler"
            :value="programRef.settings.menteeMatchingCriteria"
          />
        </div>
        <div class="settings-section">
          <p class="settings-section__title">{{ t('admin.programSettings.section.branding') }}</p>
          <p class="settings-section__description">{{ t('admin.programSettings.section.branding.description') }}</p>
          <CustomInput
            type="url"
            id="program-logo"
            data-test-id="program-logo"
            :label="t('admin.programSettings.form.logo.label')"
            :placeholder="t('admin.programSettings.form.logo.placeholder')"
            @emit:change="logoChangeHandler"
            :value="programRef.settings.logo || ''"
          />
          <div class="program-settings-form__grid">
            <CustomInput
              type="url"
              id="program-logo-small"
              data-test-id="program-logo-small"
              :label="t('admin.programSettings.form.logoSmall.label')"
              :placeholder="t('admin.programSettings.form.logoSmall.placeholder')"
              @emit:change="logoSmallChangeHandler"
              :value="programRef.settings.logoSmall || ''"
            />
            <CustomInput
              type="url"
              id="program-favicon"
              data-test-id="program-favicon"
              :label="t('admin.programSettings.form.favicon.label')"
              :placeholder="t('admin.programSettings.form.favicon.placeholder')"
              @emit:change="faviconChangeHandler"
              :value="programRef.settings.favicon || ''"
            />
          </div>
          <div class="program-settings-form__grid">
            <div>
              <CustomInput
                type="color"
                id="program-color-primary"
                data-test-id="program-color-primary"
                :label="t('admin.programSettings.form.colorPrimary.label')"
                :placeholder="t('admin.programSettings.form.colorPrimary.placeholder')"
                @emit:change="colorPrimaryChangeHandler"
                :value="programRef.settings.colorPrimary || ''"
              />
              <ColorContrastProposal :hex="programRef.settings.colorPrimary" @accept="colorPrimaryChangeHandler" />
            </div>
            <div>
              <CustomInput
                type="color"
                id="program-color-secondary"
                data-test-id="program-color-secondary"
                :label="t('admin.programSettings.form.colorSecondary.label')"
                :placeholder="t('admin.programSettings.form.colorSecondary.placeholder')"
                @emit:change="colorSecondaryChangeHandler"
                :value="programRef.settings.colorSecondary || ''"
              />
              <ColorContrastProposal :hex="programRef.settings.colorSecondary" @accept="colorSecondaryChangeHandler" />
            </div>
          </div>
        </div>
        <div class="settings-section">
          <p class="settings-section__title" data-test-id="session-program-setting">
            {{ t('admin.programSettings.section.session') }}
          </p>
          <CustomCheckbox
            id="mentee-can-book-session"
            data-test-id="checkbox-mentee-can-book-session"
            :label="t('admin.programSettings.form.menteeCanBookSession.label')"
            @emit:change="onMenteeCanBookSession"
            :defaultChecked="programRef.settings.menteeCanBookSession"
          />
          <CustomCheckbox
            id="should-review-session-rating"
            data-test-id="checkbox-mentee-can-rate-session"
            :label="t('admin.programSettings.form.shouldReviewSessionRating.label')"
            @emit:change="onShouldReviewSessionRatingChange"
            :defaultChecked="programRef.settings.shouldReviewSessionRating"
          />
          <CustomCheckbox
            id="should-review-session-review"
            data-test-id="checkbox-mentor-review-session-review"
            :label="t('admin.programSettings.form.shouldReviewSessionReview.label')"
            @emit:change="onShouldReviewSessionReviewChange"
            :defaultChecked="programRef.settings.shouldReviewSessionReview"
          />
          <CustomCheckbox
            id="should-mentor-review-session-presence"
            data-test-id="checkbox-mentor-review-session-presence"
            :label="t('admin.programSettings.form.shouldMentorReviewSessionPresence.label')"
            @emit:change="onShouldMentorReviewSessionPresenceChange"
            :defaultChecked="programRef.settings.shouldMentorReviewSessionPresence"
          />
          <CustomCheckbox
            id="should-mentor-review-session-deliverable"
            data-test-id="checkbox-mentor-review-session-deliverable"
            :label="t('admin.programSettings.form.shouldReviewSessionDeliverable.label')"
            @emit:change="onShouldReviewSessionDeliverableChange"
            :defaultChecked="programRef.settings.shouldReviewSessionDeliverable"
          />
          <div style="margin-left: 24px">
            <CustomCheckbox
              id="should-mentor-review-session-deliverable-rate"
              data-test-id="checkbox-mentor-review-session-deliverable-rate"
              :label="t('admin.programSettings.form.shouldMentorReviewSessionDeliverableRate.label')"
              @emit:change="onShouldMentorReviewSessionDeliverableRateChange"
              :defaultChecked="programRef.settings.shouldMentorReviewSessionDeliverableRate"
            />
          </div>

          <CustomCheckbox
            id="should-mentor-review-session-deliverable-report"
            data-test-id="checkbox-mentor-review-session-deliverable-report"
            :label="t('admin.programSettings.form.shouldMentorReviewSessionDeliverableReport.label')"
            @emit:change="onShouldMentorReviewSessionDeliverableReportChange"
            :defaultChecked="programRef.settings.shouldMentorReviewSessionDeliverableReport"
          />
          <CustomTextarea
            id="mentor-report-template"
            :label="t('admin.programSettings.form.mentorReportTemplate.label')"
            :placeholder="t('admin.programSettings.form.mentorReportTemplate.placeholder')"
            :rows="5"
            @emit:change="mentorReportTemplateChangeHandler"
            :value="programRef.settings.mentorReportTemplate"
          />
          <CustomCheckbox
            id="should-mentee-review-session-report"
            data-test-id="checkbox-mentee-review-session-report"
            :label="t('admin.programSettings.form.shouldMenteeReviewSessionReport.label')"
            @emit:change="onShouldMenteeReviewSessionReportChange"
            :defaultChecked="programRef.settings.shouldMenteeReviewSessionReport"
          />
          <CustomTextarea
            id="mentee-report-template"
            :label="t('admin.programSettings.form.menteeReportTemplate.label')"
            :placeholder="t('admin.programSettings.form.menteeReportTemplate.placeholder')"
            :rows="5"
            @emit:change="menteeReportTemplateChangeHandler"
            :value="programRef.settings.menteeReportTemplate"
          />
        </div>
        <SessionObjectivesSettings :programId="selectedProgramIdRef" />
        <div class="settings-section--actions">
          <CustomButton
            usage="button"
            type="button"
            color="primary"
            :text="t('ui.button.save')"
            data-test-id="admin-program-settings-save-button"
            :isLoading="isUpdatingPendingRef || isCreatingPendingRef"
            @emit:click="saveProgram"
          />
        </div>
      </div>
    </template>
  </GridBlockBase>
</template>

<script setup lang="ts">
import { useMutation } from '@tanstack/vue-query'
import moment from 'moment-timezone'
import type { PropType } from 'vue'
import { computed, ref, watch } from 'vue'

import ProgramTypeSetting from './program-type-setting.vue'
import SessionObjectivesSettings from './session-objectives-settings.vue'

import ColorContrastProposal from '@/features/admin/settings/components/color-contrast-proposal.vue'
import useAccount from '@/features/auth/hooks/use-account.hook'
import { createProgramSettingsMutation, updateProgramSettingsMutation } from '@/features/programs/api'
import useProgram from '@/features/programs/hooks/use-program.hook'
import {
  selectGetSelectedPeriodId,
  selectGetSelectedProgramId,
} from '@/features/programs/stores/programs/programs.selectors'
import programsStore from '@/features/programs/stores/programs/programs.store'
import type { ProgramType } from '@/features/programs/types'
import CustomButton from '@/features/ui/components/button/custom-button.vue'
import CustomCheckbox from '@/features/ui/components/checkbox/custom-checkbox.vue'
import CustomTextarea from '@/features/ui/components/custom-textarea/custom-textarea.vue'
import GridBlockBase from '@/features/ui/components/grid-block-base/grid-block-base.vue'
import CustomInput from '@/features/ui/components/input/custom-input.vue'
import TiptapEditor from '@/features/ui/components/tiptap-editor/tiptap-editor.vue'
import useRouter from '@/hooks/use-router.hook'
import useToast from '@/hooks/use-toasts.hook'
import { useI18n } from '@/lib/i18n'
import { ROUTES } from '@/utils/config/constants'
import { resetMainQueriesCache } from '@/utils/lib/vue-query'

const props = defineProps({
  id: {
    type: String as PropType<string | null>,
    default: null,
  },
})
const router = useRouter()
const toast = useToast()
const { t } = useI18n()

const { accountRef } = useAccount()
const selectedProgramIdRef = selectGetSelectedProgramId(programsStore)
const { programRef: selectedProgramRef } = useProgram(selectedProgramIdRef)

const emptyProgram = {
  id: null,
  name: '',
  description: '',
  periods: [],
  settings: {
    i18nMentorName: 'Mentor',
    i18nMenteeName: 'Mentee',
    adminDashboardType: 'MATCHING',
    programType: undefined,
    defaultInactivityDays: 30,
    maxUserPerGroup: 3,
    maxGroupPerMentee: 3,
    maxGroupPerMentor: 3,
    matchingCriteriaEnabled: false,
    matchingAutoSuggestEnabled: false,
    matchingAdditionalInstructions: '',
    mentorMatchingCriteria: '',
    menteeMatchingCriteria: '',
    mentorReportTemplate: '',
    shouldReviewSessionRating: false,
    shouldReviewSessionReview: false,
    menteeCanBookSession: true,
    shouldMentorReviewSessionPresence: false,
    shouldReviewSessionDeliverable: false,
    shouldMentorReviewSessionDeliverableRate: false,
    shouldMentorReviewSessionDeliverableReport: false,
    hasMentorReviewSessionDeliverableReport: false,
    hasMentorReviewSessionDeliverableRate: false,
    hasMentorReviewSessionPresence: false,
    hasReviewSessionRating: false,
    hasReviewSessionReview: false,
    hasReviewSessionDeliverable: false,
    moderationPiiEnabled: false,
    logo: undefined,
    logoSmall: undefined,
    favicon: undefined,
    colorPrimary: undefined,
    colorSecondary: undefined,
  },
}

const programRef = computed(() => (!props.id || props.id.length < 1 ? emptyProgram : selectedProgramRef.value))

const programDescriptionRef = computed({
  get: () => programRef.value?.description ?? '',
  set: (value: string) => {
    if (programRef.value) {
      programRef.value.description = value
    }
  },
})

const selectedPeriodIdRef = selectGetSelectedPeriodId(programsStore)
const selectedPeriodRef = computed(() =>
  selectedProgramRef.value?.periods.find((period) => period.id === selectedPeriodIdRef.value),
)
const emptyPeriod = { id: null, startAt: '', endAt: '' }
const periodRef = computed(() => (!props.id || props.id.length < 1 ? emptyPeriod : selectedPeriodRef.value))

const startAtRef = ref('')
const endAtRef = ref('')

const formattedStartAtRef = computed(() => startAtRef.value)
const formattedEndAtRef = computed(() => endAtRef.value)

watch(
  periodRef,
  (newPeriod) => {
    if (newPeriod) {
      startAtRef.value = moment(newPeriod.startAt).format('YYYY-MM-DD') || ''
      endAtRef.value = moment(newPeriod.endAt).format('YYYY-MM-DD') || ''
    }
  },
  { immediate: true },
)

function programNameChangeHandler(value: string) {
  programRef.value!.name = value
}
function mentorNameChangeHandler(value: string) {
  programRef.value!.settings.i18nMentorName = value
}
function menteeNameChangeHandler(value: string) {
  programRef.value!.settings.i18nMenteeName = value
}
function startAtChangeHandler(value: string) {
  startAtRef.value = value
}
function endAtChangeHandler(value: string) {
  endAtRef.value = value
}
function defaultInactivityDaysChangeHandler(value: string) {
  programRef.value!.settings.defaultInactivityDays = Number(value)
}
function maxUserPerGroupChangeHandler(value: string) {
  programRef.value!.settings.maxUserPerGroup = Number(value)
}
function maxGroupPerMentorChangeHandler(value: string) {
  programRef.value!.settings.maxGroupPerMentor = Number(value)
}
function maxGroupPerMenteeChangeHandler(value: string) {
  programRef.value!.settings.maxGroupPerMentee = Number(value)
}
function matchingAdditionalInstructionsChangeHandler(value: string) {
  programRef.value!.settings.matchingAdditionalInstructions = value
}
function mentorMatchingCriteriaChangeHandler(value: string) {
  programRef.value!.settings.mentorMatchingCriteria = value
}
function menteeMatchingCriteriaChangeHandler(value: string) {
  programRef.value!.settings.menteeMatchingCriteria = value
}
function mentorReportTemplateChangeHandler(value: string) {
  programRef.value!.settings.mentorReportTemplate = value
}
function onMatchingCriteriaEnabledChange(newValue: boolean) {
  programRef.value!.settings.matchingCriteriaEnabled = newValue
  // Si on désactive les critères, désactiver aussi l'auto-suggest
  if (!newValue) {
    programRef.value!.settings.matchingAutoSuggestEnabled = false
  }
}
function onMatchingAutoSuggestEnabledChange(newValue: boolean) {
  programRef.value!.settings.matchingAutoSuggestEnabled = newValue
}
function onShouldReviewSessionRatingChange(newValue: boolean) {
  programRef.value!.settings.shouldReviewSessionRating = newValue
}
function onMenteeCanBookSession(newValue: boolean) {
  programRef.value!.settings.menteeCanBookSession = newValue
}
function onShouldReviewSessionReviewChange(newValue: boolean) {
  programRef.value!.settings.shouldReviewSessionReview = newValue
}
function onShouldMentorReviewSessionPresenceChange(newValue: boolean) {
  programRef.value!.settings.shouldMentorReviewSessionPresence = newValue
}
function onShouldReviewSessionDeliverableChange(newValue: boolean) {
  programRef.value!.settings.shouldReviewSessionDeliverable = newValue
}
function onShouldMentorReviewSessionDeliverableRateChange(newValue: boolean) {
  programRef.value!.settings.shouldMentorReviewSessionDeliverableRate = newValue
}
function onShouldMentorReviewSessionDeliverableReportChange(newValue: boolean) {
  programRef.value!.settings.shouldMentorReviewSessionDeliverableReport = newValue
}
function onShouldMenteeReviewSessionReportChange(newValue: boolean) {
  programRef.value!.settings.shouldMenteeReviewSessionReport = newValue
}
function menteeReportTemplateChangeHandler(value: string) {
  programRef.value!.settings.menteeReportTemplate = value
}

const { mutate: updateProgramSettings, isPending: isUpdatingPendingRef } = useMutation({
  mutationFn: updateProgramSettingsMutation,
  onError: () => {
    // toast?.error(t('error.common'))
    console.log('mutation error')
  },
  onSuccess: async () => {
    toast?.success(t('admin.programSettings.toast.updateSuccess'))
    await resetMainQueriesCache(selectedProgramIdRef.value!)
  },
})

const { mutate: createProgramSettings, isPending: isCreatingPendingRef } = useMutation({
  mutationFn: createProgramSettingsMutation,
  onError: () => {
    toast?.error(t('admin.programSettings.toast.createError'))
  },
  onSuccess: async (result: any) => {
    await resetMainQueriesCache(selectedProgramIdRef.value!)
    programsStore.setSelectedProgramId(result.program.id)
    router?.push(ROUTES.ADMIN.EDIT_PROGRAM_SETTINGS)
  },
})

function programTypeSavedHandler(type: ProgramType | undefined) {
  programRef.value!.settings.programType = type
}

function onModerationPiiEnabledChange(newValue: boolean) {
  programRef.value!.settings.moderationPiiEnabled = newValue
}

function logoChangeHandler(value: string) {
  programRef.value!.settings.logo = value && value.trim() !== '' ? value.trim() : undefined
}
function logoSmallChangeHandler(value: string) {
  programRef.value!.settings.logoSmall = value && value.trim() !== '' ? value.trim() : undefined
}
function faviconChangeHandler(value: string) {
  programRef.value!.settings.favicon = value && value.trim() !== '' ? value.trim() : undefined
}
function colorPrimaryChangeHandler(value: string) {
  programRef.value!.settings.colorPrimary = value && value.trim() !== '' ? value.trim() : undefined
}
function colorSecondaryChangeHandler(value: string) {
  programRef.value!.settings.colorSecondary = value && value.trim() !== '' ? value.trim() : undefined
}

function saveProgram() {
  const formattedStartAt = moment(startAtRef.value).startOf('day').format('YYYY-MM-DD HH:mm:ssZ')
  const formattedEndAt = moment(endAtRef.value).endOf('day').format('YYYY-MM-DD HH:mm:ssZ')

  const payload = {
    globalSettings: {
      programName: programRef.value?.name,
      programDescription: programRef.value?.description ?? null,
      selectedPeriodId: selectedPeriodIdRef.value!,
      programPeriodStartAt: formattedStartAt,
      programPeriodEndAt: formattedEndAt,
    },
    settings: {
      mentorMatchingCriteria: programRef.value?.settings.mentorMatchingCriteria ?? '',
      menteeMatchingCriteria: programRef.value?.settings.menteeMatchingCriteria,
      mentorReportTemplate: programRef.value?.settings.mentorReportTemplate,
      menteeReportTemplate: programRef.value?.settings.menteeReportTemplate,
      matchingCriteriaEnabled: programRef.value?.settings.matchingCriteriaEnabled ?? false,
      matchingAutoSuggestEnabled: programRef.value?.settings.matchingAutoSuggestEnabled ?? false,
      matchingAdditionalInstructions: programRef.value?.settings.matchingAdditionalInstructions ?? '',
      maxUserPerGroup: programRef.value?.settings.maxUserPerGroup ?? 1,
      maxGroupPerMentor: programRef.value?.settings.maxGroupPerMentor ?? 1,
      maxGroupPerMentee: programRef.value?.settings.maxGroupPerMentee ?? 1,
      shouldMentorReviewSessionDeliverableReport:
        programRef.value?.settings.shouldMentorReviewSessionDeliverableReport ?? false,
      shouldMenteeReviewSessionReport: programRef.value?.settings.shouldMenteeReviewSessionReport ?? false,
      shouldMentorReviewSessionDeliverableRate:
        programRef.value?.settings.shouldMentorReviewSessionDeliverableRate ?? false,
      shouldMentorReviewSessionPresence: programRef.value?.settings.shouldMentorReviewSessionPresence ?? false,
      shouldReviewSessionRating: programRef.value?.settings.shouldReviewSessionRating ?? false,
      shouldReviewSessionReview: programRef.value?.settings.shouldReviewSessionReview ?? false,
      shouldReviewSessionDeliverable: programRef.value?.settings.shouldReviewSessionDeliverable ?? false,
      adminDashboardType: programRef.value?.settings.adminDashboardType ?? 'MATCHING',
      defaultInactivityDays: programRef.value?.settings.defaultInactivityDays ?? 0,
      i18nMenteeName: programRef.value?.settings.i18nMenteeName ?? '',
      i18nMentorName: programRef.value?.settings.i18nMentorName ?? '',
      menteeCanBookSession: programRef.value?.settings.menteeCanBookSession ?? false,
      moderationPiiEnabled: programRef.value?.settings.moderationPiiEnabled ?? false,
      hasMentorReviewSessionDeliverableReport:
        programRef.value?.settings.hasMentorReviewSessionDeliverableReport ?? false,
      hasMentorReviewSessionDeliverableRate: programRef.value?.settings.hasMentorReviewSessionDeliverableRate ?? false,
      hasMentorReviewSessionPresence: programRef.value?.settings.hasMentorReviewSessionPresence ?? false,
      hasReviewSessionRating: programRef.value?.settings.hasReviewSessionRating ?? false,
      hasReviewSessionReview: programRef.value?.settings.hasReviewSessionReview ?? false,
      hasReviewSessionDeliverable: programRef.value?.settings.hasReviewSessionDeliverable ?? false,
      logo: programRef.value?.settings.logo,
      logoSmall: programRef.value?.settings.logoSmall,
      favicon: programRef.value?.settings.favicon,
      colorPrimary: programRef.value?.settings.colorPrimary,
      colorSecondary: programRef.value?.settings.colorSecondary,
    },
  }

  if (programRef.value?.id) {
    updateProgramSettings({
      ...payload,
      globalSettings: {
        ...payload.globalSettings,
        programId: programRef.value.id,
      },
    })
  } else {
    createProgramSettings({
      ...payload,
    })
  }
}
</script>

<style scoped lang="scss">
.program-settings-form {
  display: flex;
  flex-direction: column;
  gap: 2.5rem;

  &__grid {
    display: grid;
    gap: 1rem;
    align-items: flex-end;
    grid-template-columns: repeat(2, 1fr);
  }
}

.settings-section {
  border-top: #2d374830 solid 1px;
  padding: 2rem 2rem 1.5rem 2rem;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.settings-section__title {
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
  color: #2d3748;
}

.settings-section__description {
  font-size: 0.875rem;
  color: #718096;
  margin-bottom: 1rem;
  line-height: 1.4;
}

.settings-section--actions {
  border-top: rgba(45, 55, 72, 0.1882352941) solid 1px;
  padding: 2rem 2rem 1.5rem 0rem;
  display: flex;
  justify-content: flex-end;
}
</style>
